import { ensureNotNil, generateTrackingId } from "client/Misc/utils";
import {
	KeyboardEvent,
	PropsWithChildren,
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState
} from "react";
import { ActionListHeader } from "../ActionList/ActionListHeader";
import {
	Button,
	ButtonKind,
	ButtonSize
} from "../Button/Button";
import {
	ItemResponsiveBehavior
} from "../CollapsibleList/CollapsibleList";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import {
	DropDownDirection,
	ToolbarContext,
	ToolbarPriority
} from "./Toolbar";
import { ToolbarItem } from "./ToolbarItem";
import { FontIconName, SvgIconValue } from "../Icon/Icon";
import { mergeRefs } from "client/Misc/reactUtils";

export type ToolbarMenuButtonProps = {
	kind: ButtonKind;
	text: string;
	icon?: FontIconName | SvgIconValue;
	trackingId?: string;
	responsiveBehavior?: ItemResponsiveBehavior;
	dropOverWidth?: number;
	isDisabled?: boolean;
	rightIcon?: FontIconName;
	onDropOverClose?(): void;
};

export const ToolbarMenuButton = (props: PropsWithChildren<ToolbarMenuButtonProps>): ReactElement => {
	const {
		kind,
		text,
		icon,
		trackingId,
		responsiveBehavior,
		dropOverWidth,
		isDisabled,
		onDropOverClose,
		rightIcon,
		children
	} = props;

	const {
		dropDownDirection,
		buttonSize
	} = useContext(ToolbarContext);

	const priority = useMemo(
		(): number => {
			if (responsiveBehavior === "hide") {
				return ToolbarPriority.LowPriorityItem;
			}
			if (kind === "primary") {
				return ToolbarPriority.PrimaryButton;
			}

			return ToolbarPriority.Default;
		},
		[responsiveBehavior, kind]
	);

	return (
		<ToolbarItem
			isGroup
			priority={priority}
			responsiveBehavior={responsiveBehavior}
			isDisabled={isDisabled}
			renderNormalMode={(
				ref,
				onKeyDown,
				tabIndex
			) => (
				<NormalMode
					ref={ref}
					onKeyDown={onKeyDown}
					tabIndex={tabIndex}
					kind={kind}
					icon={icon}
					text={text}
					dropDownDirection={dropDownDirection}
					trackingId={trackingId}
					dropOverWidth={dropOverWidth}
					onDropOverClose={onDropOverClose}
					isDisabled={isDisabled}
					rightIcon={rightIcon}
					buttonSize={buttonSize}
				>
					{children}
				</NormalMode>
			)}
			renderCollapsedMode={() => (
				<CollapsedMode
					text={text}
					isDisabled={isDisabled}
				>
					{children}
				</CollapsedMode>
			)}
		/>
	);
};
ToolbarMenuButton.displayName = "Toolbar.MenuButton";

type NormalModeProps = {
	ref: (element: HTMLElement | null) => void;
	onKeyDown: (args: KeyboardEvent<HTMLElement>) => void;
	tabIndex: number;
	kind: ButtonKind;
	text: string;
	icon: FontIconName | SvgIconValue | undefined;
	trackingId: string | undefined;
	dropDownDirection: DropDownDirection;
	dropOverWidth: number | undefined;
	isDisabled: boolean | undefined;
	buttonSize: ButtonSize;
	onDropOverClose: (() => void) | undefined;
	rightIcon: FontIconName | undefined;
};

const NormalMode = (props: PropsWithChildren<NormalModeProps>): ReactElement => {
	const {
		ref,
		onKeyDown,
		tabIndex,
		kind,
		text,
		icon,
		trackingId,
		dropDownDirection,
		dropOverWidth,
		isDisabled,
		buttonSize,
		onDropOverClose,
		rightIcon,
		children
	} = props;

	const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);
	const buttonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const closeHandler = useCallback(
		(): void => {
			setIsDropDownOpen(false);
			onDropOverClose?.();
		},
		[onDropOverClose]
	);

	return (
		<>
			<Button
				ref={mergeRefs(ref, buttonRef)}
				onKeyDown={onKeyDown}
				tabIndex={tabIndex}
				kind={kind}
				text={text}
				icon={icon}
				isDisabled={isDisabled}
				onClick={() => setIsDropDownOpen(true)}
				trackingId={generateTrackingId(trackingId, "_button")}
				rightIcon={rightIcon}
				size={buttonSize}
			/>
			{isDropDownOpen && !isDisabled && (
				<DropOverActionList
					target={ensureNotNil(buttonRef.current)}
					onClose={closeHandler}
					trackingId={generateTrackingId(trackingId, "_dropdown")}
					contentAlignment={
						dropDownDirection === "down"
							? "tl"
							: "bl"
					}
					targetAlignment={
						dropDownDirection === "down"
							? "bl"
							: "tl"
					}
					width={dropOverWidth}
					menuTitle={text}
				>
					{children}
				</DropOverActionList>
			)}
		</>
	);
};
NormalMode.displayName = "NormalMode";

type CollapsedModeProps = {
	text: string;
	isDisabled: boolean | undefined;
};

const CollapsedMode = (props: PropsWithChildren<CollapsedModeProps>): ReactElement | null => {
	const {
		text,
		isDisabled,
		children
	} = props;

	return (
		<>
			<ActionListHeader
				text={text}
				isDisabled={isDisabled}
			/>
			{!isDisabled && children}
		</>
	);
};
CollapsedMode.displayName = "CollapsedMode";
